import { useCallback, useEffect, useRef, useState } from 'react';

import { animate, type AnimationPlaybackControls } from 'framer-motion';

import { roundToCommonDecimals } from '@pkg/utils';

import { type AnimateNumberOptions } from './types';
import { convertMsToSec, convertSecToMs } from './utilities';

export type AnimateNumberReturn = ReturnType<typeof useAnimateNumber>;

export function useAnimateNumber(
    from: number,
    to: number,
    { duration, roundValue, onUpdate, onComplete }: AnimateNumberOptions,
) {
    // Value is the one data point we keep as a `ref`,
    // as it is used within `onUpdate()` and will change rapidly.
    // Most likely, the consumer will pass a state updater to
    // `onUpdate()` anyways, defeating this optimization. But,
    // in case they want to avoid re-renders and don't record
    // state, this is a useful optimization.
    const valueRef = useRef(from);
    const animationRef = useRef<AnimationPlaybackControls>();

    const [timeRemaining, setTimeRemaining] = useState(convertMsToSec(duration));
    const [progress, setProgress] = useState(0);

    const [pause, setPause] = useState(true);
    const [reverse, setReverse] = useState(false);
    const [resetCount, setResetCount] = useState(0);

    const handleReset = useCallback(() => {
        // Requires calling `stop` in order to correctly
        // reset the call to `animate()`. The `useEffect()`
        // will re-initialize it.
        animationRef.current?.stop();
        valueRef.current = from;

        setProgress(0);
        setTimeRemaining(convertMsToSec(duration));
        setResetCount((count) => count + 1);
    }, [from, duration]);

    // It is too easy for consumer's to forget to
    // memoize their callbacks, so we will do it for them.
    const updateRef = useRef(onUpdate);
    const completeRef = useRef(onComplete);

    useEffect(() => {
        valueRef.current = from;
    }, [from]);

    useEffect(() => {
        updateRef.current = onUpdate;
    }, [onUpdate]);

    useEffect(() => {
        completeRef.current = onComplete;
    }, [onComplete]);

    useEffect(() => {
        // Rather than calculate `progress` against any of these changed props,
        // and continue animation from the last value, we keep it simple and
        // just re-start the animation.
        handleReset();
    }, [from, to, duration, handleReset]);

    useEffect(() => {
        animationRef.current = animate(valueRef.current, to, {
            duration: timeRemaining,
            onUpdate(value) {
                const parsedValue = roundValue ? Math.floor(value) : value;
                valueRef.current = parsedValue;

                updateRef.current?.(parsedValue);
            },
            onStop() {
                // In case `from` does not begin at `0`:
                const fromDifference = valueRef.current - from;
                const toDifference = to - from;

                const currentProgress = fromDifference / toDifference;
                const elapsed = duration * currentProgress;

                const roundedProgress = roundToCommonDecimals(currentProgress * 100);
                const roundedTime = roundToCommonDecimals(duration - elapsed);

                setProgress(roundedProgress);
                setTimeRemaining(convertMsToSec(roundedTime));
            },
            onComplete() {
                setPause(true);
                completeRef.current?.();
            },
        });

        // Required to run this in addition to the `teardown`,
        // as we are initializing `pause` as `true` and want to
        // immediately tell `framer-motion` to `stop` the animation.
        if (pause) animationRef.current.stop();

        return () => animationRef.current?.stop();
    }, [from, to, duration, timeRemaining, roundValue, progress, pause, resetCount]);

    return {
        start() {
            setPause(false);
        },
        stop() {
            setPause(true);
        },
        reverse() {
            // eslint-disable-next-line no-console
            console.log('useAnimateNumber > reverse() is not yet implemented!');
            setReverse((current) => !current);
        },
        reset() {
            handleReset();
        },
        isPlaying() {
            // TODO: AnimationPlaybackControls no longer supports `isAnimating()`.
            return false;
        },
        isReversed() {
            return reverse;
        },
        getValue() {
            return valueRef.current;
        },
        getProgress() {
            return progress;
        },
        getTimeRemaining() {
            return convertSecToMs(timeRemaining);
        },
    };
}
